import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { fmtDate, fmtPrice } from "@/lib/format";
import { PageTitle, Loader, Empty, Section, Stat, Field } from "@/components/xd/ui";
import { useAct } from "./Admin";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";

const ALL = "__all";

const DemoToggle = ({ value, onChange, testid }) => {
  const { t } = useLang();
  return <label className="flex items-center gap-2 text-xs text-zinc-400"><Switch checked={value} onCheckedChange={onChange} data-testid={testid} />{t("demo.include")}</label>;
};

const AttributionDialog = ({ inq, onDone }) => {
  const { t } = useLang();
  const act = useAct();
  const [open, setOpen] = useState(false);
  const [h, setH] = useState(null);
  const [uid, setUid] = useState(inq.broker_uid || "");
  const [reason, setReason] = useState("");
  useEffect(() => { if (open) api.get(`/admin/inquiries/${inq.id}/history`).then((r) => setH(r.data)); }, [open, inq.id]);
  const save = async () => { if (await act(() => api.put(`/admin/inquiries/${inq.id}/attribution`, { broker_uid: uid.trim() || null, reason }))) { setOpen(false); onDone(); } };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><button className="text-xs underline text-zinc-400 hover:text-white" data-testid={`attribution-open-${inq.code}`}>{t("inq.dispute")}</button></DialogTrigger>
      <DialogContent className="bg-black border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="attribution-dialog">
        <DialogHeader><DialogTitle className="text-start">{t("inq.dispute")} · <span className="ltr-num">{inq.code}</span></DialogTitle></DialogHeader>
        {!h ? <Loader /> : (
          <div className="space-y-5">
            <div className="max-h-56 overflow-y-auto border border-white/10">
              <table className="xd-table"><thead><tr><th>{t("common.date")}</th><th>{t("inq.event")}</th><th>{t("inq.broker")}</th><th>UTM</th></tr></thead>
                <tbody>{h.events.map((e) => <tr key={e.id}><td className="ltr-num text-xs">{fmtDate(e.created_at)}</td><td className="text-xs">{t(`ev.${e.type}`)}</td><td className="ltr-num text-xs">{e.broker_uid || "—"}</td><td className="ltr-num text-xs">{e.utm?.utm_source || ""} {e.utm?.utm_campaign || ""}</td></tr>)}</tbody></table>
            </div>
            {h.history.length > 0 && <div className="text-xs text-zinc-400 space-y-1">{h.history.map((x, i) => <p key={i} className="ltr-num">{fmtDate(x.at)} · {x.by} · {x.reason}</p>)}</div>}
            <Field label={t("inq.credited_broker")} hint={t("inq.empty_removes")}><Input dir="ltr" placeholder="B-1001" className="xd-input" value={uid} onChange={(e) => setUid(e.target.value)} data-testid="attribution-broker-input" /></Field>
            <Field label={t("common.reason")}><Textarea rows={2} className="xd-input" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="attribution-reason-input" /></Field>
            <button className="btn-xd w-full" disabled={reason.trim().length < 3} onClick={save} data-testid="attribution-save-button">{t("common.save")}</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Pick = ({ value, onChange, options, testid, prefix }) => {
  const { t } = useLang();
  return (
    <Select value={value || ALL} onValueChange={(v) => onChange(v === ALL ? "" : v)}>
      <SelectTrigger className="xd-input w-40" data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent><SelectItem value={ALL}>{t("filter.all")}</SelectItem>{options.map((o) => <SelectItem key={o} value={o}>{t(`${prefix}.${o}`)}</SelectItem>)}</SelectContent>
    </Select>
  );
};

export function AdminInquiries() {
  const { t } = useLang();
  const { user } = useAuth();
  const act = useAct();
  const [status, setStatus] = useState("");
  const [type, setType] = useState("");
  const [demo, setDemo] = useState(false);
  const [d, setD] = useState(null);
  const load = () => api.get("/admin/inquiries", { params: { include_demo: demo, ...(status && { status }), ...(type && { type }) } }).then((r) => setD(r.data));
  useEffect(() => { load(); }, [status, type, demo]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div>
      <PageTitle eyebrow={t("admin.leads")} title={t("admin.inquiries")}>
        <div className="flex flex-wrap items-center gap-3">
          <Pick value={status} onChange={setStatus} options={["new", "contacted", "closed"]} prefix="istatus" testid="inq-status-filter" />
          <Pick value={type} onChange={setType} options={["whatsapp", "similar", "callback"]} prefix="itype" testid="inq-type-filter" />
          <DemoToggle value={demo} onChange={setDemo} testid="inq-include-demo" />
        </div>
      </PageTitle>
      {!d ? <Loader /> : !d.items.length ? <Empty text={t("common.none")} /> : (
        <div className="xd-panel overflow-x-auto">
          <table className="xd-table">
            <thead><tr><th>{t("inq.code")}</th><th>{t("inq.type")}</th><th>{t("common.vehicle")}</th><th>{t("common.date")}</th><th>{t("inq.source")}</th><th>{t("inq.contact")}</th>{d.attribution_visible && <th>{t("inq.broker")}</th>}<th>{t("inq.status")}</th></tr></thead>
            <tbody>
              {d.items.map((i) => (
                <tr key={i.id} data-testid={`inquiry-row-${i.code}`}>
                  <td className="ltr-num font-medium">{i.code}{i.is_demo && <span className="block text-[10px] text-amber-400">DEMO</span>}</td>
                  <td className="text-xs">{t(`itype.${i.type}`)}</td>
                  <td><Link className="hover:underline" to={`/admin/vehicles/${i.vehicle_id}`}>{i.vehicle_title}</Link><span className="block text-xs text-zinc-500 ltr-num">{i.vehicle_code} · {fmtPrice(i.advertised_price)}</span></td>
                  <td className="ltr-num text-xs whitespace-nowrap">{fmtDate(i.created_at)}</td>
                  <td className="ltr-num text-xs">{i.utm?.utm_source || "direct"}{i.utm?.utm_campaign ? ` / ${i.utm.utm_campaign}` : ""}</td>
                  <td className="text-xs">{i.name ? <>{i.name}<span className="block ltr-num">{i.phone}</span>{i.preferred_time && <span className="block text-zinc-500">{i.preferred_time}</span>}</> : "—"}</td>
                  {d.attribution_visible && <td className="text-xs"><span className="ltr-num" data-testid={`inquiry-broker-${i.code}`}>{i.broker_uid || "—"}</span>{i.broker_name && <span className="block text-zinc-500">{i.broker_name}</span>}{user.is_super && <AttributionDialog inq={i} onDone={load} />}</td>}
                  <td>
                    <Select value={i.status} onValueChange={(s) => act(() => api.put(`/admin/inquiries/${i.id}/status`, { status: s })).then(load)}>
                      <SelectTrigger className="h-9 w-32 text-xs" data-testid={`inquiry-status-${i.code}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{["new", "contacted", "closed"].map((s) => <SelectItem key={s} value={s}>{t(`istatus.${s}`)}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const MiniTable = ({ cols, rows, keyOf }) => (
  <div className="overflow-x-auto"><table className="xd-table"><thead><tr>{cols.map(([, l]) => <th key={l}>{l}</th>)}</tr></thead>
    <tbody>{rows.map((r) => <tr key={keyOf(r)}>{cols.map(([k, l]) => <td key={l} className="ltr-num">{typeof k === "function" ? k(r) : r[k] || 0}</td>)}</tr>)}</tbody></table></div>
);

export function AdminAnalytics() {
  const { t } = useLang();
  const [days, setDays] = useState("30");
  const [demo, setDemo] = useState(false);
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); api.get("/admin/analytics", { params: { days, include_demo: demo } }).then((r) => setD(r.data)); }, [days, demo]);
  const ev = [["visit", t("ev.visit")], ["vehicle_view", t("ev.vehicle_view")], ["whatsapp_click", t("ev.whatsapp_click")], ["similar_click", t("ev.similar_click")], ["callback", t("ev.callback")]];
  return (
    <div className="space-y-6">
      <PageTitle eyebrow={t("admin.insights")} title={t("admin.analytics")}>
        <div className="flex flex-wrap items-center gap-3">
        <DemoToggle value={demo} onChange={setDemo} testid="analytics-include-demo" />
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="xd-input w-36" data-testid="analytics-days"><SelectValue /></SelectTrigger>
          <SelectContent>{["7", "30", "90", "365"].map((x) => <SelectItem key={x} value={x}>{t("analytics.days", { n: x })}</SelectItem>)}</SelectContent>
        </Select>
        </div>
      </PageTitle>
      <p className="text-xs text-zinc-500 -mt-4" data-testid="analytics-scope-note">{demo ? t("demo.including") : t("demo.excluded")} {t("demo.wa_note")}</p>
      {!d ? <Loader /> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            <Stat label={t("analytics.unique")} value={d.totals.unique_visitors} testid="an-unique" />
            <Stat label={t("ev.visit")} value={d.totals.visit} testid="an-visits" />
            <Stat label={t("ev.vehicle_view")} value={d.totals.vehicle_view} testid="an-views" />
            <Stat label={t("ev.referral_visit")} value={d.totals.referral_visit} testid="an-referrals" />
            <Stat label={t("ev.whatsapp_click")} value={d.totals.whatsapp_click} testid="an-whatsapp" />
            <Stat label={t("ev.similar_click")} value={d.totals.similar_click || 0} testid="an-similar" />
            <Stat label={t("ev.callback")} value={d.totals.callback} testid="an-callbacks" />
          </div>
          <Section title={t("analytics.daily")}>
            <div className="h-72" dir="ltr">
              <ResponsiveContainer><LineChart data={d.daily}><CartesianGrid stroke="#1c1c1c" /><XAxis dataKey="date" stroke="#71717a" fontSize={11} /><YAxis stroke="#71717a" fontSize={11} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#000", border: "1px solid #333" }} /><Legend />
                {ev.map(([k, l], i) => <Line key={k} type="monotone" dataKey={k} name={l} stroke={["#e4e4e7", "#a1a1aa", "#ff3b30", "#f59e0b"][i]} dot={false} strokeWidth={2} />)}
              </LineChart></ResponsiveContainer>
            </div>
          </Section>
          <Section title={t("analytics.funnel")} testid="analytics-funnel">
            {[["visitors", d.funnel.visitors], ["viewed_vehicle", d.funnel.viewed_vehicle], ["enquired", d.funnel.enquired]].map(([k, n]) => (
              <div key={k} className="space-y-1"><div className="flex justify-between text-sm"><span>{t(`funnel.${k}`)}</span><span className="ltr-num">{n}</span></div>
                <div className="h-2 bg-white/5"><div className="h-full bg-gradient-to-l from-[var(--xd-red)] to-zinc-300" style={{ width: `${d.funnel.visitors ? (n / d.funnel.visitors) * 100 : 0}%` }} /></div></div>
            ))}
          </Section>
          <Section title={t("analytics.campaigns")}>
            {d.campaigns.length ? <MiniTable keyOf={(r) => `${r.source}${r.campaign}`} rows={d.campaigns} cols={[["source", "Source"], ["campaign", "Campaign"], ...ev.filter(([k]) => k !== "vehicle_view")]} /> : <p className="text-sm text-zinc-500">{t("common.none")}</p>}
          </Section>
          <Section title={t("analytics.top_vehicles")}>
            {d.top_vehicles.length ? <MiniTable keyOf={(r) => r.code} rows={d.top_vehicles} cols={[[(r) => `${r.title} · ${r.code}`, t("common.vehicle")], ...ev.slice(1)]} /> : <p className="text-sm text-zinc-500">{t("common.none")}</p>}
          </Section>
          {d.brokers && (
            <Section title={t("analytics.brokers")} testid="analytics-brokers">
              {d.brokers.length ? <MiniTable keyOf={(r) => r.uid} rows={d.brokers} cols={[[(r) => `${r.uid} · ${r.name}`, t("inq.broker")], ["referral_visit", t("ev.referral_visit")], ["whatsapp_click", t("ev.whatsapp_click")], ["callback", t("ev.callback")]]} /> : <p className="text-sm text-zinc-500">{t("common.none")}</p>}
            </Section>
          )}
        </>
      )}
    </div>
  );
}
